import { z } from 'zod';

/**
 * Health Check & Network Diagnostics Types
 *
 * System for monitoring connectivity to external services
 * and diagnosing network performance issues.
 */

/**
 * Service health status
 */
export const ServiceHealthStatusSchema = z.enum([
  'healthy', // Service is operational with good latency
  'degraded', // Service is slow but accessible
  'down', // Service is completely unreachable
  'blocked', // Service appears to be blocked (firewall/proxy)
  'unknown', // Unable to determine status
]);

export type ServiceHealthStatus = z.infer<typeof ServiceHealthStatusSchema>;

/**
 * External service identifiers
 */
export const ExternalServiceSchema = z.enum([
  'openai', // OpenAI API (most critical)
  's3', // AWS S3 storage
  'workos', // WorkOS authentication
  'stripe', // Stripe billing
  'sentry', // Sentry error tracking
]);

export type ExternalService = z.infer<typeof ExternalServiceSchema>;

/**
 * Service health check result
 */
export const ServiceHealthCheckSchema = z.object({
  service: ExternalServiceSchema,
  serviceName: z.string(), // Display name
  serviceUrl: z.string(), // URL tested
  status: ServiceHealthStatusSchema,
  latency: z.number().optional(), // Response time in ms
  timestamp: z.string(), // ISO timestamp
  error: z.string().optional(), // Error message if failed
  suggestion: z.string().optional(), // User-friendly suggestion
});

export type ServiceHealthCheck = z.infer<typeof ServiceHealthCheckSchema>;

/**
 * VPN Detection Confidence Level
 */
export type VPNConfidence = 'none' | 'low' | 'medium' | 'high' | 'very-high';

/**
 * Firewall Detection Confidence Level
 */
export type FirewallConfidence = 'none' | 'low' | 'medium' | 'high' | 'very-high';

/**
 * Complete health check report
 */
export const HealthCheckReportSchema = z.object({
  timestamp: z.string(),
  overallStatus: ServiceHealthStatusSchema,
  services: z.array(ServiceHealthCheckSchema),
  networkInfo: z.object({
    isOnline: z.boolean(),
    effectiveBandwidth: z.string().optional(), // "4g", "3g", "slow-2g", etc.
    downlink: z.number().optional(), // Mbps
    rtt: z.number().optional(), // Round-trip time in ms
  }),
  detectedIssues: z.array(
    z.object({
      type: z.enum(['vpn', 'proxy', 'firewall', 'dns', 'bandwidth', 'latency']),
      severity: z.enum(['low', 'medium', 'high']),
      description: z.string(),
      suggestion: z.string(),
      confidence: z.enum(['none', 'low', 'medium', 'high', 'very-high']).optional(), // Detection confidence
    })
  ),
});

export type HealthCheckReport = z.infer<typeof HealthCheckReportSchema>;

/**
 * Performance issue details for bug reports
 */
export const PerformanceIssueDetailsSchema = z.object({
  issueType: z.enum([
    'slow_response', // AI responses are slow
    'app_freeze', // App becomes unresponsive
    'slow_loading', // Initial load is slow
    'streaming_slow', // Text streaming appears slow
    'network_timeout', // Requests timing out
    'other', // Other performance issue
  ]),
  healthCheckReport: HealthCheckReportSchema.optional(), // Auto-run diagnostics
  recentResponseTimes: z.array(z.number()).optional(), // Last N response times
  averageResponseTime: z.number().optional(), // Average in seconds
});

export type PerformanceIssueDetails = z.infer<typeof PerformanceIssueDetailsSchema>;

/**
 * Service display information
 */
export type ServiceInfo = {
  id: ExternalService;
  name: string;
  url: string;
  testEndpoint: string; // Endpoint to test (should be fast, like health check)
  critical: boolean; // Is this service critical for app function?
  timeout: number; // Max time to wait for response (ms)
};

/**
 * Service configuration map
 */
export const SERVICE_INFO: Record<ExternalService, ServiceInfo> = {
  openai: {
    id: 'openai',
    name: 'OpenAI API',
    url: 'https://api.openai.com',
    testEndpoint: 'https://api.openai.com', // Simple HEAD request to base URL (doesn't require auth)
    critical: true,
    timeout: 5000,
  },
  s3: {
    id: 's3',
    name: 'AWS S3',
    url: 'https://s3.amazonaws.com',
    testEndpoint: 'https://s3.amazonaws.com', // Simple HEAD request
    critical: true,
    timeout: 5000,
  },
  workos: {
    id: 'workos',
    name: 'WorkOS',
    url: 'https://api.workos.com',
    testEndpoint: 'https://api.workos.com', // Simple HEAD request
    critical: true,
    timeout: 5000,
  },
  stripe: {
    id: 'stripe',
    name: 'Stripe',
    url: 'https://api.stripe.com',
    testEndpoint: 'https://api.stripe.com', // Simple HEAD request
    critical: false,
    timeout: 5000,
  },
  sentry: {
    id: 'sentry',
    name: 'Sentry',
    url: 'https://sentry.io',
    testEndpoint: 'https://sentry.io/api/0/', // Public API
    critical: false,
    timeout: 3000,
  },
};

/**
 * Performance thresholds
 */
export const PERFORMANCE_THRESHOLDS = {
  HEALTHY_LATENCY: 200, // < 200ms = healthy
  DEGRADED_LATENCY: 500, // 200-500ms = degraded
  // > 500ms = slow/problematic

  HEALTHY_RESPONSE_TIME: 10, // < 10s = healthy AI response
  SLOW_RESPONSE_TIME: 30, // > 30s = slow AI response

  HIGH_RTT: 100, // > 100ms round-trip time
  VERY_HIGH_RTT: 300, // > 300ms round-trip time
} as const;

/**
 * User-friendly status messages
 */
export const STATUS_MESSAGES: Record<ServiceHealthStatus, { icon: string; label: string }> = {
  healthy: { icon: '🟢', label: 'Operational' },
  degraded: { icon: '🟡', label: 'Slow' },
  down: { icon: '🔴', label: 'Unreachable' },
  blocked: { icon: '🚫', label: 'Blocked' },
  unknown: { icon: '⚪', label: 'Unknown' },
};

/**
 * Common issue suggestions
 */
export const COMMON_SUGGESTIONS = {
  VPN_DETECTED: 'VPN connection detected. Try disconnecting temporarily to improve speed.',
  FIREWALL_BLOCKING: 'Corporate firewall may be blocking connections. Contact your IT department.',
  SLOW_NETWORK:
    'Your network connection is slow. Try switching to a faster network or moving closer to your router.',
  DNS_ISSUES:
    'DNS resolution is slow. Try changing your DNS servers to 8.8.8.8 (Google) or 1.1.1.1 (Cloudflare).',
  PROXY_DETECTED:
    'HTTP proxy detected. Proxy servers can slow down connections. Try direct connection if possible.',
  HIGH_LATENCY:
    'High network latency detected. This is often caused by distance to servers or network congestion.',
} as const;
